// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ImageSourcePropType } from 'react-native';

import { getAssetImageSource } from '@/services/assetImageSource';

const WaterIcon = getAssetImageSource('images/water.png', 'icon', '/round/[roundId]/hole/[hole]');
const BunkerIcon = getAssetImageSource('images/bunker.png', 'icon', '/round/[roundId]/hole/[hole]');
const PenaltyIcon = getAssetImageSource('images/penalties.png', 'icon', '/round/[roundId]/hole/[hole]');
const ThreePuttIcon = getAssetImageSource('images/threeputt.png', 'icon', '/round/[roundId]/hole/[hole]');
const MysteryPrizeIcon = getAssetImageSource('images/mystery.png', 'icon', '/round/[roundId]/hole/[hole]');
const GolfballIcon = getAssetImageSource('images/golfball.png', 'icon', '/round/[roundId]/hole/[hole]');
// Chip-in has no dedicated artwork yet; reuse the golf ball icon for the toggle.
const ChipInIcon = getAssetImageSource('images/golfball.png', 'icon', '/round/[roundId]/hole/[hole]');
// Out of bounds has no dedicated artwork yet; reuse the penalty icon for the toggle. The row
// carries its own label, so it reads as Out rather than as a second Penalty counter.
const OutIcon = getAssetImageSource('images/penalties.png', 'icon', '/round/[roundId]/hole/[hole]');
// The animated critters shown next to a player who picked up hazards. The preset must stay empty
// and no width/height may be passed: asset-resizer only streams the original bytes when both are
// absent. Any other request re-encodes the GIF through ImageSharp into an *animated* WebP, which
// Android will not play (expo.webp.animated=false) — so it neither animates nor freezes cleanly.
const HazardCritterImage = getAssetImageSource('images/hazard-critter.gif', '', '/round/[roundId]/hole/[hole]');
const HazardCritterBunkerImage = getAssetImageSource('images/hazard-critter-bunker.gif', '', '/round/[roundId]/hole/[hole]');
const HazardCritterWaterImage = getAssetImageSource('images/hazard-critter-water.gif', '', '/round/[roundId]/hole/[hole]');
const HazardCritterPenaltyImage = getAssetImageSource('images/hazard-critter-penalty.gif', '', '/round/[roundId]/hole/[hole]');
const HazardCritterOutImage = getAssetImageSource('images/hazard-critter-out.gif', '', '/round/[roundId]/hole/[hole]');
// Frozen companions for the score buttons, where the animal is shown but must hold still. They are
// separate files rather than a preset on the GIF, for the reason above.
const HazardCritterStillImage = getAssetImageSource('images/hazard-critter-still.png', 'icon', '/round/[roundId]/hole/[hole]');
const HazardCritterBunkerStillImage = getAssetImageSource('images/hazard-critter-bunker-still.png', 'icon', '/round/[roundId]/hole/[hole]');
const HazardCritterWaterStillImage = getAssetImageSource('images/hazard-critter-water-still.png', 'icon', '/round/[roundId]/hole/[hole]');
const HazardCritterPenaltyStillImage = getAssetImageSource('images/hazard-critter-penalty-still.png', 'icon', '/round/[roundId]/hole/[hole]');
const HazardCritterOutStillImage = getAssetImageSource('images/hazard-critter-out-still.png', 'icon', '/round/[roundId]/hole/[hole]');
const BirdieImage = getAssetImageSource('images/birdie.png', '', '/round/[roundId]/hole/[hole]', {
  width: 512,
  height: 512,
  bucketStep: 1,
});
const BestBallSelectImage = getAssetImageSource('images/games/best-ball-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const BunkerSelectImage = getAssetImageSource('images/games/bunker-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const HighLowSelectImage = getAssetImageSource('images/games/high-low-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const MysterySelectImage = getAssetImageSource('images/games/mystery-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const SkinsSelectImage = getAssetImageSource('images/games/skins-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const StablefordSelectImage = getAssetImageSource('images/games/stableford-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const WaterSelectImage = getAssetImageSource('images/games/water-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
// Out and Scramble ship with per-game copies of the penalty and best-ball artwork until their
// own art is drawn — per-game S3 paths, per the asset ownership rule.
const OutSelectImage = getAssetImageSource('images/games/out-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const ScrambleSelectImage = getAssetImageSource('images/games/scramble-select.png', 'game-select', '/round/[roundId]/hole/[hole]');
const ScoreButtonImage = getAssetImageSource('images/button-score.png', 'button', '/round/[roundId]/hole/[hole]');
const ScoreButtonSelectedImage = getAssetImageSource('images/button-score-clicked.png', 'button', '/round/[roundId]/hole/[hole]');
const TrophyImage = getAssetImageSource('images/trophy.png', 'icon', '/round/[roundId]/hole/[hole]');
const TrophyOnlyImage = getAssetImageSource('images/trophy_only.png', 'icon', '/round/[roundId]/hole/[hole]');
const PuttButtonImage = getAssetImageSource('images/button-putt.png', 'button', '/round/[roundId]/hole/[hole]');
const ScorePopupShortButtonImage = getAssetImageSource('images/short_button.png', '', '/round/[roundId]/hole/[hole]', { width: 203, height: 62, bucketStep: 1 });
const ScorePopupLargeButtonImage = getAssetImageSource('images/large_button.png', '', '/round/[roundId]/hole/[hole]', { width: 100, height: 50, bucketStep: 1 });
const ScorePopupLongButtonImage = getAssetImageSource('images/long_button.png', '', '/round/[roundId]/hole/[hole]', { width: 430, height: 85, bucketStep: 1 });
const ScorePopupRailBannerImage = getAssetImageSource(
  'images/long_Banner.png',
  '',
  '/round/[roundId]/hole/[hole]',
  { width: 60, height: 480, bucketStep: 1 },
);
const LeftButtonImage = getAssetImageSource('images/left_button.png', '', '/round/[roundId]/hole/[hole]', { width: 80, height: 80, bucketStep: 1 });
const LeftButtonDisabledImage = getAssetImageSource('images/left_button-disabled.png', '', '/round/[roundId]/hole/[hole]', { width: 80, height: 80, bucketStep: 1 });
const RightButtonImage = getAssetImageSource('images/right_button.png', '', '/round/[roundId]/hole/[hole]', { width: 80, height: 80, bucketStep: 1 });

const GAME_ICON_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': BestBallSelectImage,
  bunker: BunkerSelectImage,
  freeze: MysterySelectImage,
  'high-low': HighLowSelectImage,
  mystery: MysterySelectImage,
  out: OutSelectImage,
  scramble: ScrambleSelectImage,
  skins: SkinsSelectImage,
  stableford: StablefordSelectImage,
  'stroke-play': StablefordSelectImage,
  water: WaterSelectImage,
};

// Chip in is not a hazard — it is the one thing in that column worth being pleased about — so it
// never gets a critter and keeps its golf ball wherever hazards are drawn.
type HazardSymbolType = 'bunker' | 'water' | 'penalty' | 'out' | 'threeputt';

const HAZARD_SYMBOL_ORDER: HazardSymbolType[] = ['bunker', 'water', 'penalty', 'out', 'threeputt'];

// The flat symbol every hazard has, used wherever animations are turned off.
const HAZARD_ICON_BY_TYPE: Record<HazardSymbolType, ImageSourcePropType> = {
  bunker: BunkerIcon,
  water: WaterIcon,
  penalty: PenaltyIcon,
  out: OutIcon,
  threeputt: ThreePuttIcon,
};

// A species per hazard: white rabbit in the bunker, blue fish in the water, red crab for a
// penalty, gold bird flying off out of bounds, and the gopher wrecking the green for a 3-putt.
const HAZARD_CRITTER_BY_TYPE: Record<HazardSymbolType, ImageSourcePropType> = {
  bunker: HazardCritterBunkerImage,
  water: HazardCritterWaterImage,
  penalty: HazardCritterPenaltyImage,
  out: HazardCritterOutImage,
  threeputt: HazardCritterImage,
};

const HAZARD_CRITTER_STILL_BY_TYPE: Record<HazardSymbolType, ImageSourcePropType> = {
  bunker: HazardCritterBunkerStillImage,
  water: HazardCritterWaterStillImage,
  penalty: HazardCritterPenaltyStillImage,
  out: HazardCritterOutStillImage,
  threeputt: HazardCritterStillImage,
};

// hazardTypeFromName() yields 'threeputt' while the summary's normalizeHazardType() yields
// 'threePutt'; both collapse to the same key here so callers need not care which produced it.
const normalizeHazardSymbolKey = (type: string) => type.replace(/[\s_-]+/g, '').toLowerCase();

/**
 * The symbol for a hazard on the score input popup: the animal holding still, or the flat icon
 * when the player has animations off. Frozen deliberately — that popup is a dense grid of
 * controls, and five looping animals in it is a fidget spinner, not a scorecard.
 */
function hazardScoreInputSymbol(type: HazardSymbolType, animated: boolean): ImageSourcePropType {
  return animated ? HAZARD_CRITTER_STILL_BY_TYPE[type] : HAZARD_ICON_BY_TYPE[type];
}

/** The looping animal for one hazard, for the places that do animate. */
function hazardCritterForType(type: HazardSymbolType): ImageSourcePropType {
  return HAZARD_CRITTER_BY_TYPE[type];
}

/**
 * The symbols for every hazard a player picked up, for the places that do animate — the hole
 * leaderboard row and the round summary. One per hazard, in a fixed order.
 */
function hazardSymbolsForTypes(types: Iterable<string>, animated: boolean): ImageSourcePropType[] {
  return hazardTypesInOrder(types).map((type) => (animated ? HAZARD_CRITTER_BY_TYPE : HAZARD_ICON_BY_TYPE)[type]);
}

/** A player's hazards in display order, dropping anything without a symbol of its own (chip in). */
function hazardTypesInOrder(types: Iterable<string>): HazardSymbolType[] {
  const present = new Set(Array.from(types, normalizeHazardSymbolKey));
  return HAZARD_SYMBOL_ORDER.filter((type) => present.has(type));
}

export type { HazardSymbolType };

export {
  BirdieImage,
  BestBallSelectImage,
  BunkerIcon,
  BunkerSelectImage,
  ChipInIcon,
  GAME_ICON_MAP,
  GolfballIcon,
  HazardCritterBunkerImage,
  HazardCritterBunkerStillImage,
  HazardCritterImage,
  HazardCritterOutImage,
  HazardCritterOutStillImage,
  HazardCritterPenaltyImage,
  HazardCritterPenaltyStillImage,
  HazardCritterStillImage,
  HazardCritterWaterImage,
  HazardCritterWaterStillImage,
  hazardCritterForType,
  hazardScoreInputSymbol,
  hazardSymbolsForTypes,
  hazardTypesInOrder,
  HighLowSelectImage,
  MysteryPrizeIcon,
  MysterySelectImage,
  OutIcon,
  OutSelectImage,
  PenaltyIcon,
  PuttButtonImage,
  ScoreButtonImage,
  ScoreButtonSelectedImage,
  ScorePopupLargeButtonImage,
  LeftButtonImage,
  LeftButtonDisabledImage,
  RightButtonImage,
  ScorePopupLongButtonImage,
  ScorePopupRailBannerImage,
  ScorePopupShortButtonImage,
  ScrambleSelectImage,
  SkinsSelectImage,
  StablefordSelectImage,
  ThreePuttIcon,
  TrophyImage,
  TrophyOnlyImage,
  WaterIcon,
  WaterSelectImage,
};
